/**
 * "Desktop notifications" control for a settings screen (TRUS-668): turns
 * browser push on (asks for permission) or off (unsubscribes this browser).
 * Renders nothing where web push cannot work (desktop app, pop-out windows,
 * browsers without the Push API).
 */
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useWebPushControl, type WebPushPortal } from '@/hooks/useWebPush';
import { getLastWebPushFailure } from '@/lib/webPush';
import { toastWebPushFailure, webPushFailureText } from '@/components/notifications/webPushFailureText';
import { usePushMasterSwitch } from '@/hooks/usePushMasterSwitch';

export function WebPushSetting({ portal }: { portal: WebPushPortal }) {
  const { t } = useAppTranslation();
  const { status, failure, busy, enable, disable } = useWebPushControl(portal);
  // TRUS-697: "Push notifications on all your devices" off -> no push reaches
  // this browser either, so don't offer to turn it on here.
  const { enabled: pushOnAllDevices } = usePushMasterSwitch(portal === 'staff');
  const masterOff = portal === 'staff' && pushOnAllDevices === false;

  if (status === 'unsupported') return null;

  let detail: string;
  switch (masterOff ? 'masterOff' : status) {
    case 'masterOff':
      detail = t('messaging.webPush.statusMasterOff', 'Off in your notification settings.');
      break;
    case 'subscribed':
      detail = t('messaging.webPush.statusOn', 'On for this browser. Messages and calls show up even when WeaveIn is in the background.');
      break;
    case 'denied':
      detail = t('messaging.webPush.statusBlocked', 'Blocked in this browser. Allow notifications for this site in the browser settings, then come back.');
      break;
    case 'disabled':
      detail = t('messaging.webPush.statusUnavailable', 'Not available yet: browser notifications are not set up for your organization.');
      break;
    case 'error':
      // Why it failed (TRUS-668); the generic line only when nothing is known.
      detail = failure ? webPushFailureText(failure, t) : t('messaging.webPush.statusError', 'Something went wrong. Try again.');
      break;
    case 'loading':
      detail = t('messaging.webPush.statusLoading', 'Checking...');
      break;
    default:
      detail = t('messaging.webPush.statusOff', 'Off. Turn on to get a notification on this computer when a message or call comes in.');
  }

  // A browser that cannot receive push gets no retry button.
  const canEnable = !masterOff && (status === 'default' || (status === 'error' && failure?.kind !== 'unsupported'));

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700" data-testid="web-push-setting">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">{t('messaging.webPush.settingTitle', 'Browser notifications')}</p>
          <p
            className="text-sm text-gray-600 dark:text-gray-400"
            role={status === 'error' ? 'alert' : undefined}
            data-testid="web-push-setting-detail"
          >
            {detail}
          </p>
        </div>
        {status === 'subscribed' ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void disable()}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            {t('messaging.webPush.disable', 'Turn off')}
          </button>
        ) : canEnable ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void enable().then((s) => {
                if (s === 'error' || s === 'denied') toastWebPushFailure(getLastWebPushFailure(), t);
              });
            }}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {t('messaging.webPush.enable', 'Enable desktop notifications')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default WebPushSetting;
